<script setup>
import { ref, computed, watch } from 'vue';
import FloatingSelect from '@/components/FloatingSelect.vue';

// Option disabled logic

const isOptionDisabled = (option) => {
  if (typeof props.optionDisabled === 'function') return props.optionDisabled(option);
  if (typeof props.optionDisabled === 'boolean') return props.optionDisabled;
  return false;
};

// Expose for template (for <script setup> context)
defineExpose({ isOptionDisabled });

const emit = defineEmits(['update:modelValue', 'change']);
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  keyProp: { type: String, default: 'id' },
  labelKey: { type: [String, Function], default: 'name' },
  placeholder: { type: String, default: 'Auswahl treffen' },
  ariaLabel: { type: String, default: 'Mehrfachauswahl' },
  disabled: { type: Boolean, default: false },
  searchProp: { type: [String, Array, Function], default: 'name' },
  size: { type: String, default: 'sm' },
  matchTriggerWidth: { type: Boolean, default: true },
  maxSelected: { type: Number, default: 4 },
  optionDisabled: { type: [Function, Boolean], default: undefined },
});

const normalizedOptions = computed(() => Array.isArray(props.options)
  ? props.options.filter(o => o?.[props.keyProp] !== undefined)
  : []
);

const selectedIds = ref([]);
watch(() => props.modelValue, (val) => {
  selectedIds.value = Array.isArray(val) ? [...val] : [];
}, { immediate: true });

function labelOf(option){
  if (!option) return '';
  if (typeof props.labelKey === 'function') return String(props.labelKey(option));
  return String(option?.[props.labelKey] ?? '');
}

function isSelected(option){
  const id = option?.[props.keyProp];
  return selectedIds.value.some(v => String(v) === String(id));
}

function toggleOption(option){
  if (!option) return;
  const id = option[props.keyProp];
  const idx = selectedIds.value.findIndex(v => String(v) === String(id));
  if (idx >= 0) {
    selectedIds.value.splice(idx, 1);
  } else {
    if (selectedIds.value.length >= props.maxSelected) return; // enforce max
    selectedIds.value.push(id);
  }
  emit('update:modelValue', [...selectedIds.value]);
  emit('change', [...selectedIds.value]);
}

const selectedCount = computed(()=> selectedIds.value.length);
const selectedLabels = computed(()=>{
  const map = new Map(normalizedOptions.value.map(o => [String(o[props.keyProp]), labelOf(o)]));
  return selectedIds.value.map(id => map.get(String(id)) || '').filter(Boolean);
});

function clearAll(){
  selectedIds.value = [];
  emit('update:modelValue', []);
  emit('change', []);
}
</script>

<template>
  <FloatingSelect
    :model-value="null"  
    :options="normalizedOptions"
    :option-key="keyProp"
    :option-label="labelKey"
    :search-prop="searchProp"
    :size="size"
    :disabled="disabled"
    :match-trigger-width="matchTriggerWidth"
    :return-object="true"
    :close-on-select="false"
    :aria-label="ariaLabel"
    placeholder=""
  >
    <template #trigger="{ open }">
      <button type="button" class="msd-trigger" :class="['size-' + size, { 'is-open': open, 'is-disabled': disabled }]" :aria-expanded="open.toString()" :aria-label="ariaLabel">
        <span class="msd-trigger-text">
          <template v-if="selectedCount === 0">
            Bitte auswählen
          </template>
          <template v-else-if="selectedCount >= maxSelected">
            Maximum erreicht
          </template>
          <template v-else>
            {{ selectedCount }} ausgewählt
          </template>
        </span>
        <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
      </button>
    </template>
    <template #before-list>
      <div class="msd-controls">
        <button type="button" class="msd-clear" @click="clearAll" :disabled="selectedCount===0">Alle löschen</button>
        <span class="msd-hint">Max. {{ maxSelected }} auswählbar</span>
      </div>
    </template>
    <template #item="{ option, highlighted }">
      <div
        class="msd-item"
        :class="{ 'is-highlighted': highlighted, 'is-selected': isSelected(option), 'is-disabled': isOptionDisabled(option) }"
        @click.stop="!isOptionDisabled(option) && toggleOption(option)"
        :aria-disabled="isOptionDisabled(option) ? 'true' : 'false'"
      >
        <label class="msd-check">
          <input type="checkbox" :checked="isSelected(option)" :disabled="isOptionDisabled(option)" @change.prevent="toggleOption(option)" />
          <span class="label">{{ labelOf(option) }}</span>
        </label>
      </div>
    </template>
  </FloatingSelect>
</template>

<style scoped>
.msd-trigger { display:flex; align-items:center; justify-content:space-between; gap:.4rem; width:100%; padding:.45rem .6rem; border:1px solid #c3ced6; border-radius:.65rem; background:#fff; color:#13242c; }
.msd-trigger.is-disabled { opacity:.5; cursor:not-allowed; }
.msd-trigger-text { font-size: .75rem; opacity: .8; }
.msd-trigger .chevron { opacity:.7; }

.msd-controls { display:flex; align-items:center; justify-content:space-between; gap:.5rem; padding:.2rem .25rem .35rem; }
.msd-clear { font-size:.65rem; padding:.25rem .45rem; border-radius:.45rem; border:1px solid #c3ced6; background:#fff; cursor:pointer; }
.msd-clear:disabled { opacity:.4; cursor:not-allowed; }
.msd-hint { font-size:.6rem; opacity:.65; }

.msd-item { display:flex; align-items:center; gap:.5rem; padding:.35rem .45rem; border:1px solid rgba(0,0,0,0.08); border-radius:.6rem; background:rgba(255,255,255,0.04); cursor:pointer; }
.msd-item.is-selected { background:rgba(0,143,179,0.10); border-color:rgba(0,143,179,0.25); }
.msd-item.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: auto;
}
.msd-check { display:flex; align-items:center; gap:.45rem; cursor:pointer; }
.msd-check input { width:14px; height:14px; }
.msd-check .label { font-size:.7rem; }
</style>
